import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { slug } = useParams();
  const navigate = useNavigate();
  

  useEffect(() => {
    getPosts();
    updateMetaTags();
  }, []);

  useEffect(() => {
    if (selectedPost) {
      updatePostMetaTags(selectedPost);
    } else {
      updateMetaTags();
    }
  }, [selectedPost]);

  useEffect(() => {
  if (slug && posts.length > 0) {
    // Find post by slug
    const post = posts.find(p => p.slug === slug);
    if (post) {
      setSelectedPost(post);
    }
  } else if (!slug) {
    // If no slug, show blog list
    setSelectedPost(null);
  }
}, [slug, posts]);

  const getPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (data) {
        setPosts(data);
        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(post => post.category).filter(Boolean))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateMetaTags = () => {
    // Update page title
    document.title = 'Dating Tips & Relationship Advice Blog | Flirteen';
    
    // Update meta description
    updateMetaTag('description', 'Discover expert dating tips, relationship advice, and love insights on the Flirteen blog. Find your perfect match with our comprehensive dating guides.');
    
    // Update meta keywords
    updateMetaTag('keywords', 'dating tips, relationship advice, online dating, dating app, love, romance, dating guide, flirteen');
    
    // Update Open Graph tags
    updateMetaTag('og:title', 'Dating Tips & Relationship Advice Blog | Flirteen');
    updateMetaTag('og:description', 'Expert dating advice and relationship tips to help you find love');
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:url', window.location.href);
  };

  const updatePostMetaTags = (post) => {
    // Update page title
    document.title = post.meta_title || `${post.title} | Flirteen Blog`;
    
    // Update meta description
    updateMetaTag('description', post.meta_description || post.excerpt || post.title);
    
    // Update meta keywords
    updateMetaTag('keywords', post.keywords || 'dating tips, relationship advice, flirteen');
    
    // Update Open Graph tags
    updateMetaTag('og:title', post.title);
    updateMetaTag('og:description', post.excerpt || post.meta_description);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:url', `${window.location.origin}/blog/${post.slug}`);
    if (post.featured_image) {
      updateMetaTag('og:image', post.featured_image);
    }
  };

  const updateMetaTag = (name, content) => {
    // Handle different meta tag formats
    let selector = `meta[name="${name}"]`;
    if (name.startsWith('og:')) {
      selector = `meta[property="${name}"]`;
    }
    
    let metaTag = document.querySelector(selector);
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (name.startsWith('og:')) {
        metaTag.setAttribute('property', name);
      } else {
        metaTag.setAttribute('name', name);
      }
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', content);
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generateJsonLd = (post) => {
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt || post.meta_description,
      "image": post.featured_image,
      "author": {
        "@type": "Organization",
        "name": post.author || "Flirteen Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Flirteen",
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/flirteen-logo.png`
        }
      },
      "datePublished": post.created_at,
      "dateModified": post.updated_at
    };
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading blog posts...
      </div>
    );
  }

  // Single post view
  if (selectedPost) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: '20px 0'
      }}>
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(generateJsonLd(selectedPost))}
        </script>

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          padding: '0 20px'
        }}>
          {/* Back Button */}
          <button
            onClick={() => window.history.pushState({}, '', '/blog')}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              padding: '10px 20px',
              color: '#00E9FF',
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '30px',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            ← Back to Blog
          </button>

          {/* Post Header */}
          <article>
            <header style={{ marginBottom: '40px', textAlign: 'center' }}>
              {selectedPost.featured_image && (
                <img 
                  src={selectedPost.featured_image}
                  alt={selectedPost.title}
                  style={{
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'cover',
                    borderRadius: '15px',
                    marginBottom: '30px'
                  }}
                />
              )}
              
              <h1 style={{ 
                fontSize: '36px', 
                fontWeight: '700',
                background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '15px',
                lineHeight: '1.2'
              }}>
                {selectedPost.title}
              </h1>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                <span>📅 {formatDate(selectedPost.created_at)}</span>
                <span>✍️ {selectedPost.author}</span>
                {selectedPost.category && <span>🏷️ {selectedPost.category}</span>}
              </div>

              {selectedPost.excerpt && (
                <p style={{
                  fontSize: '18px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontStyle: 'italic',
                  maxWidth: '600px',
                  margin: '0 auto'
                }}>
                  {selectedPost.excerpt}
                </p>
              )}
            </header>

            {/* Post Content */}
            <div>
              <style>{`
                .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4, .blog-content h5, .blog-content h6 {
                  color: #00E9FF;
                  margin: 30px 0 15px 0;
                  font-weight: 600;
                }
                .blog-content h2 { font-size: 28px; }
                .blog-content h3 { font-size: 24px; }
                .blog-content p {
                  margin: 15px 0;
                  line-height: 1.7;
                  color: rgba(255, 255, 255, 0.9);
                }
                .blog-content ul, .blog-content ol {
                  margin: 20px 0;
                  padding-left: 25px;
                  color: rgba(255, 255, 255, 0.9);
                }
                .blog-content li {
                  margin: 8px 0;
                  line-height: 1.6;
                }
                .blog-content ul li {
                  list-style-type: disc;
                }
                .blog-content ol li {
                  list-style-type: decimal;
                }
                .blog-content a {
                  color: #00E9FF;
                  text-decoration: none;
                }
                .blog-content a:hover {
                  text-decoration: underline;
                }
                .blog-content strong {
                  font-weight: 600;
                  color: rgba(255, 255, 255, 0.95);
                }
                .blog-content em {
                  font-style: italic;
                }
                .blog-content img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 10px;
                  margin: 20px 0;
                  display: block;
                }
                .blog-content blockquote {
                  border-left: 4px solid #00E9FF;
                  padding-left: 20px;
                  margin: 25px 0;
                  font-style: italic;
                  color: rgba(255, 255, 255, 0.8);
                }
              `}</style>
              
              <div 
                className="blog-content"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '16px',
                  lineHeight: '1.8',
                  marginBottom: '40px'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: selectedPost.content 
                }}
              />
            </div>
          </article>

          {/* Share Section */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '15px',
            padding: '20px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: '#00E9FF', marginBottom: '15px' }}>
              💕 Found this helpful? Share with friends!
            </h3>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  const text = encodeURIComponent(selectedPost.title);
                  window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
                }}
                style={{
                  background: '#1DA1F2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Share on Twitter
              </button>
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
                }}
                style={{
                  background: '#4267B2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Share on Facebook
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Blog listing view
  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px 0'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px'
      }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '50px',
          paddingTop: '20px'
        }}>
          <h1 style={{ 
            fontSize: '42px', 
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '15px'
          }}>
            💕 Dating & Love Blog
          </h1>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '18px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Expert dating tips, relationship advice, and love insights to help you find your perfect match
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            marginBottom: '40px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setSelectedCategory('all')}
              style={{
                padding: '8px 20px',
                borderRadius: '20px',
                border: `2px solid ${selectedCategory === 'all' ? '#00E9FF' : 'rgba(255, 255, 255, 0.2)'}`,
                background: selectedCategory === 'all' 
                  ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                  : 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              All Posts
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '8px 20px',
                  borderRadius: '20px',
                  border: `2px solid ${selectedCategory === category ? '#00E9FF' : 'rgba(255, 255, 255, 0.2)'}`,
                  background: selectedCategory === category 
                    ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                    : 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '18px',
            marginTop: '100px'
          }}>
            <h3>📝 No posts yet!</h3>
            <p>Our dating experts are working on amazing content for you.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '30px',
            marginBottom: '50px'
          }}>
            {filteredPosts.map(post => (
              <article 
                key={post.id} 
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onClick={() => navigate(`/blog/${post.slug}`)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 233, 255, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {post.featured_image && (
                  <img 
                    src={post.featured_image}
                    alt={post.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover'
                    }}
                  />
                )}
                
                <div style={{ padding: '25px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    marginBottom: '15px',
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.6)'
                  }}>
                    <span>📅 {formatDate(post.created_at)}</span>
                    {post.category && <span>🏷️ {post.category}</span>}
                  </div>

                  <h2 style={{
                    fontSize: '22px',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '15px',
                    lineHeight: '1.3'
                  }}>
                    {post.title}
                  </h2>

                  {post.excerpt && (
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      marginBottom: '20px'
                    }}>
                      {post.excerpt}
                    </p>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      color: '#00E9FF',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      Read More →
                    </span>
                    <span style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '12px'
                    }}>
                      By {post.author}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* SEO Footer */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          padding: '30px',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ color: '#00E9FF', marginBottom: '15px' }}>
            💝 Ready to Find Love?
          </h3>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '20px',
            fontSize: '16px'
          }}>
            Join thousands of singles who found their perfect match on Flirteen
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              color: 'white',
              border: 'none',
              borderRadius: '15px',
              padding: '15px 30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Start Dating Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default Blog;